import { CONTENT_KIND_ORDER, isContentKindIncluded } from 'utils/socialCampaign/planAndContent'
import { CampaignPlanRequestDTO, ContentInventoryDTO, ContentKind, SocialCampaignDTO } from 'models/socialCampaign'
import {
  SocialCampaignTarget,
  useUpdateSocialCampaignContentKind,
  useUpdateSocialCampaignContentKindVisuals,
  useUpdateSocialCampaignPlan
} from 'dataQueries/socialCampaign.query'
import { VisualsChoice, excludedWithChoices, visualIdOf, visualKeyOf, visualsOfKind } from 'utils/socialCampaign/contentKindVisuals'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { logger } from 'utils/logging'
import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { failurePropertiesOf, trackSocialCampaignEvent } from 'utils/analytics/socialCampaign'

/** How long the section waits for the next change before saving (a second select picked right after the first). */
export const PLAN_SAVE_DELAY_MS = 400

interface Changes {
  plan?: CampaignPlanRequestDTO
  kinds: Partial<Record<ContentKind, boolean>>
  /** The visuals of a kind kept and left out, as the visuals dialog saved them (ISSUE-3651) */
  visuals: Partial<Record<ContentKind, VisualsChoice>>
}

const NO_CHANGES: Changes = { kinds: {}, visuals: {} }

const hasChanges = (changes: Changes) =>
  !!changes.plan || Object.keys(changes.kinds).length > 0 || Object.keys(changes.visuals).length > 0

const merge = (current: Changes, next: Partial<Changes>): Changes => ({
  plan: next.plan ?? current.plan,
  kinds: { ...current.kinds, ...next.kinds },
  visuals: { ...current.visuals, ...next.visuals },
})

/** Per kind, how to hand a visuals choice's Save the backend's answer, or null when there is none */
type Answers = Partial<Record<ContentKind, (answer: SocialCampaignDTO | null) => void>>

const answer = (answers: Answers, kind: ContentKind, campaign: SocialCampaignDTO | null) => {
  const resolve = answers[kind]
  delete answers[kind]
  resolve?.(campaign)
}

const choicesOf = (changes: Changes) => Object.values(changes.visuals).filter((choice): choice is VisualsChoice => !!choice)

/** Runs a write, telling a refusal with `onFailure` before passing it on (a refused write has no server event). */
const told = async <T>(write: Promise<T>, onFailure: (failure: unknown) => void) => {
  try {
    return await write
  } catch (failure) {
    onFailure(failure)
    throw failure
  }
}

/**
 * The plan sentence, the kinds of content and their visuals of a draft (ISSUE-3622, ISSUE-3651): shown as chosen at
 * once, saved a moment later, one write after the other.
 *
 * Every write re-plans the draft and the backend answers a concurrent one with a 409, so the plan, the kinds and the
 * visuals share one queue: a plan or kind change waits `PLAN_SAVE_DELAY_MS` for the next one, unless a write is on its
 * way: then it is merged with the others and sent as soon as that write answered. A choice taken back before it was
 * sent sends nothing. The visuals dialog's Save is sent at once (with whatever was waiting). `saving` holds from the
 * first change until the backend answered the last one: the drafts are being re-planned. A failure drops what was not
 * saved yet and calls `onError` (the page says it and re-reads the campaign). Leaving the page with a change still
 * waiting sends it at once; if that write fails, no page is left to say it, so the failure is logged (with the
 * campaign id) instead and the change is lost.
 *
 * `inventory` (the viewer's content) only shapes what is shown: ticking a kind back on whose every visual was left out
 * shows them all kept at once, as the backend brings them back.
 */
export function usePlanAndContentEditor(
  target: SocialCampaignTarget,
  campaign: SocialCampaignDTO | null | undefined,
  onError: () => void,
  inventory?: ContentInventoryDTO
) {
  const { mutateAsync: putPlan } = useUpdateSocialCampaignPlan()
  const { mutateAsync: putContentKind } = useUpdateSocialCampaignContentKind()
  const { mutateAsync: putVisuals } = useUpdateSocialCampaignContentKindVisuals()
  const [chosen, setChosen] = useState<Changes>(NO_CHANGES)
  const [saving, setSaving] = useState(false)
  const queued = useRef<Changes>(NO_CHANGES)
  const inFlight = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  // the campaign as the backend last had it, to skip a change that would not change anything
  const saved = useRef(campaign)
  const onErrorRef = useRef(onError)
  const isMounted = useRef(true)
  const inventoryRef = useRef(inventory)
  // the visuals choices queued, each waiting for its answer
  const answers = useRef<Answers>({})

  useEffect(() => {
    saved.current = campaign
  }, [campaign])

  useEffect(() => {
    inventoryRef.current = inventory
  }, [inventory])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const flush = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    // the answers of the choices on their way, taken with them: a Save made meanwhile waits for its own write
    let sending: Answers = {}
    try {
      while (hasChanges(queued.current)) {
        const { plan, kinds, visuals } = queued.current
        queued.current = NO_CHANGES
        sending = answers.current
        answers.current = {}
        let current = saved.current
        if (!current) break
        if (plan && (plan.posts !== current.plan.posts || plan.weeks !== current.plan.weeks)) {
          const before: SocialCampaignDTO = current
          const tracked = { posts: plan.posts, weeks: plan.weeks, previousPosts: before.plan.posts, previousWeeks: before.plan.weeks }
          current = await told(putPlan({ ...target, body: plan }), (failure) =>
            trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_PLAN_SAVED, target, before, { ...tracked, ...failurePropertiesOf(failure) }))
          saved.current = current
          trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_PLAN_SAVED, target, current, { ...tracked, outcome: 'ok' })
        }
        // before the kinds: a kind change waiting with a visuals choice of the same kind was made after it
        for (const kind of CONTENT_KIND_ORDER) {
          const choice = visuals[kind]
          if (!choice) continue
          current = await putVisuals({ ...target, kind, excludedVisuals: choice.excluded })
          saved.current = current
          answer(sending, kind, current)
        }
        for (const kind of CONTENT_KIND_ORDER) {
          const included = kinds[kind]
          if (included === undefined || isContentKindIncluded(current, kind) === included) continue
          const before: SocialCampaignDTO = current
          current = await told(putContentKind({ ...target, kind, included }), (failure) =>
            trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_CONTENT_KIND_SAVED, target, before, {
              contentKind: kind,
              included,
              ...failurePropertiesOf(failure),
            }))
          saved.current = current
          trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_CONTENT_KIND_SAVED, target, current, { contentKind: kind, included, outcome: 'ok' })
        }
      }
      setChosen(NO_CHANGES)
      CONTENT_KIND_ORDER.forEach((kind) => answer(sending, kind, null))
    } catch (error) {
      // the API layer reports the failure; the page says it and shows the campaign as the backend has it
      queued.current = NO_CHANGES
      setChosen(NO_CHANGES)
      CONTENT_KIND_ORDER.forEach((kind) => {
        answer(sending, kind, null)
        answer(answers.current, kind, null)
      })
      if (isMounted.current) onErrorRef.current()
      else logger.warn('Plan & content change lost: its save failed after the campaign tab closed', error, {
        context: { component: 'usePlanAndContentEditor', action: 'save_on_leave', metadata: { campaignId: saved.current?.id } },
      })
    } finally {
      inFlight.current = false
      setSaving(timer.current !== undefined || hasChanges(queued.current))
    }
  }, [putContentKind, putPlan, putVisuals, target])

  const flushRef = useRef(flush)
  useEffect(() => {
    flushRef.current = flush
  }, [flush])

  /** `shown` adjusts what the page shows from the choices so far, beyond the change itself */
  const change = useCallback((next: Partial<Changes>, shown: (chosen: Changes) => Changes = (chosen) => chosen) => {
    setChosen((current) => shown(merge(current, next)))
    queued.current = merge(queued.current, next)
    setSaving(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = undefined
      void flushRef.current()
    }, PLAN_SAVE_DELAY_MS)
  }, [])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (timer.current === undefined) return
      clearTimeout(timer.current)
      timer.current = undefined
      void flushRef.current()
    }
  }, [])

  const plan = {
    posts: chosen.plan?.posts ?? campaign?.plan.posts ?? 0,
    weeks: chosen.plan?.weeks ?? campaign?.plan.weeks ?? 0,
  }

  const setPlan = useCallback((next: Partial<CampaignPlanRequestDTO>) => {
    change({ plan: { posts: next.posts ?? plan.posts, weeks: next.weeks ?? plan.weeks } })
  }, [change, plan.posts, plan.weeks])

  const isIncluded = useCallback((kind: ContentKind) => {
    if (kind === ContentKind.PHOTOS) return true
    return chosen.kinds[kind] ?? (campaign ? isContentKindIncluded(campaign, kind) : true)
  }, [campaign, chosen.kinds])

  /**
   * Photos cannot be left out (the backend refuses it): ignored. Ticking a kind back on whose every visual is left out
   * brings them all back (backend and prototype v3 `include`): shown so at once, judged on the viewer's visuals (the
   * backend judges the workspace's; its answer corrects the preview).
   */
  const setIncluded = useCallback((kind: ContentKind, included: boolean) => {
    if (kind === ContentKind.PHOTOS) return
    change({ kinds: { [kind]: included } }, (chosen) => {
      const items = visualsOfKind(inventoryRef.current, kind)
      if (!included || !items.length || !saved.current) return chosen
      const leftOut = new Set(excludedWithChoices(saved.current.excludedVisuals, choicesOf(chosen)).map(visualIdOf))
      if (!items.every((item) => leftOut.has(visualIdOf(item)))) return chosen
      return merge(chosen, { visuals: { [kind]: { kept: items.map(visualKeyOf), excluded: [] } } })
    })
  }, [change])

  /**
   * The visuals dialog's Save (ISSUE-3651): sent at once, resolves with the backend's answer (null when refused, or
   * replaced by the next Save of the kind before it was sent). The write itself includes the kind or leaves it out, so
   * a kind change of the same kind still waiting is dropped. The kind is shown as the choice has it until the answer:
   * keeping one brings it back, but leaving out every visual the viewer sees leaves it out only when the workspace
   * holds no other (seat-hidden orders), which only the answer tells.
   */
  const setVisuals = useCallback((kind: ContentKind, choice: VisualsChoice): Promise<SocialCampaignDTO | null> => {
    // a Save of the kind not sent yet is replaced by this one
    answer(answers.current, kind, null)
    const answered = new Promise<SocialCampaignDTO | null>((resolve) => {
      answers.current[kind] = resolve
    })
    const kinds = kind === ContentKind.PHOTOS ? {} : { [kind]: choice.kept.length > 0 }
    setChosen((current) => merge(current, { kinds, visuals: { [kind]: choice } }))
    const waiting = { ...queued.current.kinds }
    delete waiting[kind]
    queued.current = { ...queued.current, kinds: waiting, visuals: { ...queued.current.visuals, [kind]: choice } }
    setSaving(true)
    clearTimeout(timer.current)
    timer.current = undefined
    void flushRef.current()
    return answered
  }, [])

  /** The visuals left out as shown: the campaign's, with the dialog's choices on their way */
  const excludedVisuals = useMemo(
    () => excludedWithChoices(campaign?.excludedVisuals ?? [], choicesOf(chosen)),
    [campaign?.excludedVisuals, chosen]
  )

  return { plan, setPlan, isIncluded, setIncluded, excludedVisuals, setVisuals, saving }
}
